import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronRight, ChevronDown, Home, FileText, Cpu, ClipboardPen, Settings, Shield, Code, ChevronsDownUp, PanelLeftClose, PanelLeft, FileChartColumnIncreasing, GanttChart, CircuitBoard, Wrench } from 'lucide-react';
import { navigationConfig } from '../../config/navigationConfig';
import { useAuth } from '../../context/AuthContext';

// Icon mapping for Level 1 sections
const sectionIcons = {
  'home': Home,
  'engineering': CircuitBoard,
  'documentation': FileText,
  'project-management': GanttChart,
  'equipment': Cpu,
  'data-entry': ClipboardPen,
  'configuration': Settings,
  'reports': FileChartColumnIncreasing,
  'utilities': Wrench,
  'administration': Shield,
  'development': Code
};

// Helper to find the path to a destination in the nav tree
const findPathToDestination = (items, destination, path = []) => {
  for (const item of items) {
    const currentPath = [...path, item.id];
    if (item.destination === destination) {
      return currentPath;
    }
    if (item.children) {
      const found = findPathToDestination(item.children, destination, currentPath);
      if (found) return found;
    }
  }
  return null;
};

const NavItem = ({ item, level = 1, currentPage, setCurrentPage, expandedItems, toggleExpand, isCollapsed, itemRefs }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(item.id);
  const isActive = currentPage === item.destination;
  const Icon = level === 1 ? sectionIcons[item.id] : null;
  
  const handleClick = () => {
    if (hasChildren && !isCollapsed) {
      toggleExpand(item.id);
    } else if (!item.headerOnly && item.destination) {
      setCurrentPage(item.destination);
    }
  };

  const handleLabelClick = (e) => {
    // For headerOnly items, label click just toggles expand (same as chevron)
    if (item.headerOnly) {
      e.stopPropagation();
      if (hasChildren) toggleExpand(item.id);
      return;
    }
    if (hasChildren && !isCollapsed) {
      e.stopPropagation();
      setCurrentPage(item.destination);
    }
  };

  return (
    <div className="nav-item-container">
      <button
        ref={el => { if (itemRefs) itemRefs.current[item.destination] = el; }}
        onClick={handleClick}
        className={`nav-item nav-item-level-${level} ${isActive ? 'active' : ''}`}
        data-tooltip={item.label}
      >
        <div className="nav-item-content">
          {hasChildren && !isCollapsed && (
            <span className="nav-item-chevron">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
          {!hasChildren && level > 1 && !isCollapsed && <span className="nav-item-spacer" />}
          {Icon && <Icon size={16} className="nav-item-icon" />}
          {!isCollapsed && (
            <>
              <span
                className="nav-item-label"
                onClick={hasChildren ? handleLabelClick : undefined}
              >
                {item.label}
              </span>
              {item.future && <span className="nav-future-badge">FUTURE</span>}
            </>
          )}
        </div>
      </button>
      
      {hasChildren && isExpanded && !isCollapsed && (
        <div className="nav-children">
          {item.children.map(child => (
            <NavItem
              key={child.id}
              item={child}
              level={level + 1}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              isCollapsed={isCollapsed}
              itemRefs={itemRefs}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Map nav item IDs to Admin_Users module flag property names
const dataEntryModuleMap = {
  'estimating': 'estimating',
  'integration': 'integration',
  'submittals': 'submittal',
  'sub-contractors': 'sub_Contractor',
  'purchasing': 'purchasing',
  'qaqc': 'qaqc',
  'material-transfer': 'transmittal',
  'commissioning': 'commissioning'
};

const Sidebar = ({ currentPage, setCurrentPage, sidebarTarget, clearSidebarTarget }) => {
  const { isAdmin, canAccessModule, canConfigure, currentUser } = useAuth();
  const [expandedItems, setExpandedItems] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const itemRefs = useRef({});
  const navRef = useRef(null);

  // Check if user has access to ANY data entry module
  const hasAnyDataEntryAccess = useMemo(() => {
    if (isAdmin) return true;
    if (!currentUser) return false;
    return Object.values(dataEntryModuleMap).some(flag => currentUser[flag] === true);
  }, [isAdmin, currentUser]);

  // Filter navigation items based on permissions
  const filterNavItems = useMemo(() => {
    const filterItem = (item) => {
      // Admins see everything
      if (isAdmin) {
        // Just recurse to keep structure, no filtering
        if (item.children) {
          return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        }
        return item;
      }

      // Admin-only sections (Development, Administration)
      if (item.adminOnly) return null;

      // Configuration section: PowerUser+ only
      if (item.id === 'configuration') {
        if (!canConfigure) return null;
        // If can configure, show all children
        if (item.children) {
          return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        }
        return item;
      }

      // Data Entry section: show if user has ANY module access
      if (item.id === 'data-entry') {
        if (!hasAnyDataEntryAccess) return null;
        // Filter children based on specific module flags
        if (item.children) {
          const filteredChildren = item.children.map(child => {
            const moduleFlag = dataEntryModuleMap[child.id];
            if (moduleFlag && !canAccessModule(moduleFlag)) return null;
            return filterItem(child);
          }).filter(Boolean);
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      }

      // Other sections (Documentation, Equipment, etc.): visible to all
      if (item.children) {
        const filteredChildren = item.children.map(filterItem).filter(Boolean);
        return { ...item, children: filteredChildren };
      }
      return item;
    };

    return navigationConfig.map(filterItem).filter(Boolean);
  }, [isAdmin, canConfigure, hasAnyDataEntryAccess, canAccessModule]);

  // Auto-expand and scroll when sidebarTarget changes (FK navigation)
  useEffect(() => {
    if (sidebarTarget) {
      // Find path to target in nav tree
      const path = findPathToDestination(navigationConfig, sidebarTarget);
      
      if (path) {
        // Expand all parent items (exclude the last one which is the target itself)
        const parentsToExpand = path.slice(0, -1);
        setExpandedItems(prev => {
          const newExpanded = [...prev];
          parentsToExpand.forEach(id => {
            if (!newExpanded.includes(id)) {
              newExpanded.push(id);
            }
          });
          return newExpanded;
        });

        // Scroll to the item after a brief delay to let expansion render
        setTimeout(() => {
          const targetEl = itemRefs.current[sidebarTarget];
          if (targetEl && navRef.current) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }

      // Clear the target after handling
      if (clearSidebarTarget) {
        clearSidebarTarget();
      }
    }
  }, [sidebarTarget, clearSidebarTarget]);

  const toggleExpand = (itemId) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const expandAll = () => {
    const allExpandableIds = [];
    const collectIds = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          allExpandableIds.push(item.id);
          collectIds(item.children);
        }
      });
    };
    collectIds(navigationConfig);
    setExpandedItems(allExpandableIds);
  };

  const collapseAll = () => {
    setExpandedItems([]);
  };

  const allExpanded = () => {
    const allExpandableIds = [];
    const collectIds = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          allExpandableIds.push(item.id);
          collectIds(item.children);
        }
      });
    };
    collectIds(navigationConfig);
    return allExpandableIds.every(id => expandedItems.includes(id));
  };

  // Separate Home from other nav items (using permission-filtered nav)
  const homeItem = filterNavItems.find(item => item.id === 'home');
  const otherNavItems = filterNavItems.filter(item => item.id !== 'home');

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Home row with collapse toggle */}
      {homeItem && (
        <div className="sidebar-home-row">
          <button
            ref={el => { itemRefs.current[homeItem.destination] = el; }}
            onClick={() => setCurrentPage(homeItem.destination)}
            className={`nav-item nav-item-level-1 nav-item-home ${currentPage === homeItem.destination ? 'active' : ''}`}
            data-tooltip={homeItem.label}
          >
            <div className="nav-item-content">
              <Home size={16} className="nav-item-icon" />
              {!isCollapsed && <span className="nav-item-label">{homeItem.label}</span>}
            </div>
          </button>
          <button 
            className="sidebar-collapse-toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>
      )}
      
      {/* Scrollable nav area */}
      <nav className="sidebar-nav" ref={navRef}>
        {otherNavItems.map(item => (
          <NavItem
            key={item.id}
            item={item}
            level={1}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            expandedItems={expandedItems}
            toggleExpand={toggleExpand}
            isCollapsed={isCollapsed}
            itemRefs={itemRefs}
          />
        ))}
      </nav>
      
      <div className="sidebar-footer">
        {!isCollapsed ? (
          <button
            onClick={allExpanded() ? collapseAll : expandAll}
            className="expand-toggle-btn"
          >
            <ChevronsDownUp size={14} />
            <span>{allExpanded() ? 'Collapse All' : 'Expand All'}</span>
          </button>
        ) : (
          <button
            onClick={allExpanded() ? collapseAll : expandAll}
            className="expand-toggle-btn"
            title={allExpanded() ? 'Collapse All' : 'Expand All'}
          >
            <ChevronsDownUp size={14} />
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
